import React, { Component, createRef } from "react";

import {
  Button,
  Checkbox,
  Col,
  Drawer,
  Form,
  Input,
  Row,
  Select,
  Switch,
  Table,
  Tooltip,
} from "antd";
import type { FormInstance } from "antd/es/form";
import type { CheckboxChangeEvent } from "antd/es/checkbox";
import {
  InfoCircleOutlined,
  MinusCircleOutlined,
  PlusCircleOutlined,
  RightOutlined,
} from "@ant-design/icons";

import { capitalizeString, groupAndSortByKey } from "common/utils/helpers";
import { useDrawerScrollMargin } from "common/utils/useScrollPosition";
import ActionButton from "stories/components/ActionButton/ActionButton";

const { Option } = Select;
const SELECT_ALL_LANGUAGES = "select_all_languages";

type PermissionAction = "read" | "create" | "edit" | "delete" | string;

interface CustomerApplication {
  _id: string;
  name: string;
  active?: boolean;
}

interface Dashboard {
  id: string | number;
  _id?: string | number;
  name: string;
  customerApplication?: string | number;
}

interface RolePermission {
  permissionId: string;
  read?: boolean;
  create?: boolean;
  edit?: boolean;
  delete?: boolean;
  [key: string]: any;
}

interface Role {
  id: string | number;
  _id?: string | number;
  name: string;
  description?: string;
  customerApplication?: string | number;
  isActive?: boolean;
  dashboards?: (string | number)[];
  permissions?: RolePermission[];
  [key: string]: any;
}

interface Permission {
  id: string;
  name: string;
  description?: string;
  customerApplication?: string | number;
  supportsRead?: boolean;
  supportsCreate?: boolean;
  supportsEdit?: boolean;
  supportsDelete?: boolean;
  [key: string]: any;
}

interface AddUserHandlers {
  showNewUserDrawer: () => void;
  closeNewUserDrawer: () => void;
  onFinishNewUser: (values: any) => void;
}

interface AddUserValues {
  visibleNewUserDrawer: boolean;
  newUserForm: FormInstance<any>;
  applicationsList: CustomerApplication[];
  rolesList: Role[];
  permissionsList: Permission[];
  dashboardsList: Dashboard[];
  hasCreateAccess: boolean;
  languageCodes: { title: string; value: string }[];
}

interface AddUserProps {
  handlers: AddUserHandlers;
  values: AddUserValues;
}

interface AddUserState {
  selectedCA: Set<string>; 
  selectedRoles: Set<string | number>;
  selectedPermissions: Map<
    string | number,
    {
      [action: string]: boolean;
    }
  >;
  displayedDashboards: Dashboard[];
}

class AddUser extends Component<AddUserProps, AddUserState> {
  formRef = createRef<FormInstance<any>>();
 
  state: AddUserState = {
    selectedCA: new Set(),
    selectedRoles: new Set(),
    selectedPermissions: new Map(),
    displayedDashboards: [],
  };

  /**
   * Returns [value, roleDisabled, matchedRoles]
   */
  permissionValue = (
    id: string | number,
    key: PermissionAction
  ): [boolean | undefined, boolean, Role[]] => {
    const { selectedRoles, selectedPermissions } = this.state;
    const { rolesList } = this.props.values;

    let perm: boolean | undefined;
    let roleDisabled = false;
    const matchedRoles: Role[] = [];

    Array.from(selectedRoles).forEach((rid) => {
      if (perm) {
        return;
      }
      const role = rolesList.find((r) => r.id === rid);
      if (!role || !role.permissions) return;

      role.permissions.forEach((p) => {
        if (p.permissionId === id) {
          const v = (p as any)[key];
          perm = v;
          roleDisabled = !!v;
          if (roleDisabled) {
            matchedRoles.push(role);
          }
        }
      });
    });

    if (!perm) {
      if (
        selectedPermissions &&
        selectedPermissions instanceof Map &&
        selectedPermissions.get(id) &&
        selectedPermissions.get(id)![key] !== undefined
      ) {
        perm = selectedPermissions.get(id)![key];
      }
    }

    return [perm, roleDisabled, matchedRoles];
  };

  closeDrawer = (closeNewUserDrawer: () => void) => {
    closeNewUserDrawer();
    this.setState({
      selectedCA: new Set(),
      selectedRoles: new Set(),
      selectedPermissions: new Map(),
      displayedDashboards: [],
    });
  };

  handleChangeApplications = (e: (string | number)[]) => {
    const { rolesList, dashboardsList, newUserForm } = this.props.values;

    this.setState({ selectedCA: new Set(e as string[]) });

    const rolesWithDetails: Role[] = [];

    Array.from(this.state.selectedRoles).forEach((rid) => {
      const role = rolesList.find((r) => r.id === rid);
      if (role) rolesWithDetails.push(role);
    });

    const newRoles = rolesWithDetails
      .filter((role) => e.includes(role.customerApplication as any))
      .map((role) => role.id);

    this.setState({ selectedRoles: new Set(newRoles) });

    if (this.formRef.current) {
      newUserForm.setFieldsValue({
        rolesValue: newRoles,
      });
    }

    if (Array.isArray(e) && e.length === 0 && this.formRef?.current) {
      newUserForm.setFieldsValue({
        dashboardsValue: [],
      });
      this.setState({ displayedDashboards: [] });
    } else {
      const filteredDashboardsList = dashboardsList.filter((dashboard) =>
        e.includes(dashboard.customerApplication as any)
      );
      if (filteredDashboardsList.length === 0 && this.formRef?.current) {
        newUserForm.setFieldsValue({
          dashboardsValue: [],
        });
        this.setState({ displayedDashboards: [] });
      } else {
        this.setState({ displayedDashboards: filteredDashboardsList });
      }
    }
  };

  handleChangeRoles = (e: (string | number)[]) => {
    const { dashboardsList, rolesList, newUserForm } = this.props.values;
    const { selectedCA } = this.state;

    const newSelectedRoles = new Set(e);

    const dashboardsFromRoles: (string | number)[] = [];
    Array.from(newSelectedRoles).forEach((rid) => {
      const role = rolesList.find((r) => r._id === rid || r.id === rid);
      if (role?.dashboards && Array.isArray(role.dashboards)) {
        role.dashboards.forEach((d) => {
          if (!dashboardsFromRoles.includes(d)) dashboardsFromRoles.push(d);
        });
      }
    });

    const filteredDashboardsList = dashboardsList.filter((dashboard) =>
      Array.from(selectedCA).includes(
        dashboard.customerApplication as string
      )
    );

    const finalDashboards =
      dashboardsFromRoles.length > 0
        ? dashboardsList.filter((dashboard) =>
            dashboardsFromRoles.includes(dashboard._id || dashboard.id)
          )
        : filteredDashboardsList;

    if (newUserForm) {
      if (dashboardsFromRoles.length > 0) {
        newUserForm.setFieldsValue({ dashboardsValue: dashboardsFromRoles });
      } else {
        newUserForm.setFieldsValue({ dashboardsValue: [] });
      }
    }

    this.setState({
      selectedRoles: newSelectedRoles,
      displayedDashboards: finalDashboards,
    });
  };

  onChangePermission = (e: CheckboxChangeEvent, id: string | number) => {
    const { selectedPermissions } = this.state;

    const name = e.target.name as string;
    const checked = e.target.checked;

    if (selectedPermissions.has(id)) {
      const p = selectedPermissions.get(id);
      selectedPermissions.set(id, { ...(p || {}), [name]: checked });
    } else {
      selectedPermissions.set(id, { [name]: checked });
    }

    this.setState({ selectedPermissions });
  };

  columns = [
    {
      title: "",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "",
      dataIndex: "description",
      key: "description",
      width: 10,
      render: (_text: any, record: Permission) => (
        <Tooltip
          title={record.description}
          color={"var(--tertiary-color)"}
          placement={"left"}
        >
          <InfoCircleOutlined style={{ color: "var(--tertiary-color)" }} />
        </Tooltip>
      ),
    },
    {
      title: "Read",
      dataIndex: "read",
      key: "read",
      width: 60,
      render: (_text: any, record: Permission) => {
        const [val, roleDisabled, matchedRoles] = this.permissionValue(
            record.id,
            "read"
          ),
          displayedRoles = matchedRoles
            ?.map((role) => capitalizeString(role.name))
            .join(", ");

        return record.supportsRead ? (
          <Tooltip
            placement={"left"}
            title={
              roleDisabled
                ? matchedRoles.length > 1
                  ? `This permission is part of the following roles: ${displayedRoles}. Remove roles to allow changing permission.`
                  : `This permission is part of the following role: ${displayedRoles}. Remove role to allow changing permission.`
                : ""
            }
          >
            <div>
              <Form.Item
                name={`${roleDisabled ? "role-" : ""}permissions~read-${record.id}`}
                key={`permissions~read-${record.id}`}
                noStyle
                valuePropName="checked"
                initialValue={roleDisabled && val}
              >
                <Checkbox
                  name="read"
                  disabled={roleDisabled}
                />
              </Form.Item>
            </div>
          </Tooltip>
        ) : (
          <Form.Item
            name={`permissions~read-${record.id}`}
            key={`permissions~read-${record.id}`}
            noStyle
            valuePropName="checked"
          >
            <Tooltip
              title={`This permission does not support this change.`}
              placement={"left"}
            >
              <Checkbox name="read" disabled />
            </Tooltip>
          </Form.Item>
        );
      },
    },
    {
      title: "Create",
      dataIndex: "create",
      key: "create",
      width: 60,
      render: (_text: any, record: Permission) => {
        const [val, roleDisabled, matchedRoles] = this.permissionValue(
            record.id,
            "create"
          ),
          displayedRoles = matchedRoles
            ?.map((role) => capitalizeString(role.name))
            .join(", ");
        return record.supportsCreate ? (
          <Tooltip
            placement={"left"}
            title={
              roleDisabled
                ? matchedRoles.length > 1
                  ? `This permission is part of the following roles: ${displayedRoles}. Remove roles to allow changing permission.`
                  : `This permission is part of the following role: ${displayedRoles}. Remove role to allow changing permission.`
                : ""
            }
          >
            <div>
              <Form.Item
                name={`${roleDisabled ? "role-" : ""}permissions~create-${record.id}`}
                key={`permissions~create-${record.id}`}
                noStyle
                valuePropName="checked"
                initialValue={roleDisabled && val}
              >
                <Checkbox
                  name="create"
                  disabled={roleDisabled}
                />
              </Form.Item>
            </div>
          </Tooltip>
        ) : (
          <Form.Item
            name={`permissions~create-${record.id}`}
            key={`permissions~create-${record.id}`}
            noStyle
            valuePropName="checked"
          >
            <Tooltip
              title={`This permission does not support this change.`}
              placement={"left"}
            >
              <Checkbox name="create" disabled />
            </Tooltip>
          </Form.Item>
        );
      },
    },
    {
      title: "Edit",
      dataIndex: "edit",
      key: "edit",
      width: 60,
      render: (_text: any, record: Permission) => {
        const [val, roleDisabled, matchedRoles] = this.permissionValue(
            record.id,
            "edit"
          ),
          displayedRoles = matchedRoles
            ?.map((role) => capitalizeString(role.name))
            .join(", ");
        return record.supportsEdit ? (
          <Tooltip
            placement={"left"}
            title={
              roleDisabled
                ? matchedRoles.length > 1
                  ? `This permission is part of the following roles: ${displayedRoles}. Remove roles to allow changing permission.`
                  : `This permission is part of the following role: ${displayedRoles}. Remove role to allow changing permission.`
                : ""
            }
          >
            <div>
              <Form.Item
                name={`${roleDisabled ? "role-" : ""}permissions~edit-${record.id}`}
                key={`permissions~edit-${record.id}`}
                noStyle
                valuePropName="checked"
                initialValue={roleDisabled && val}
              >
                <Checkbox
                  name="edit"
                  disabled={roleDisabled}
                />
              </Form.Item>
            </div>
          </Tooltip>
        ) : (
          <Form.Item
            name={`permissions~edit-${record.id}`}
            key={`permissions~edit-${record.id}`}
            noStyle
            valuePropName="checked"
          >
            <Tooltip
              title={`This permission does not support this change.`}
              placement={"left"}
            >
              <Checkbox name="edit" disabled />
            </Tooltip>
          </Form.Item>
        );
      },
    },
    {
      title: "Delete",
      dataIndex: "delete",
      key: "delete",
      width: 60,
      render: (_text: any, record: Permission) => {
        const [val, roleDisabled, matchedRoles] = this.permissionValue(
            record.id,
            "delete"
          ),
          displayedRoles = matchedRoles
            ?.map((role) => capitalizeString(role.name))
            .join(", ");
        return record.supportsDelete ? (
          <Tooltip
            placement={"left"}
            title={
              roleDisabled
                ? matchedRoles.length > 1
                  ? `This permission is part of the following roles: ${displayedRoles}. Remove roles to allow changing permission.`
                  : `This permission is part of the following role: ${displayedRoles}. Remove role to allow changing permission.`
                : ""
            }
          >
            <div>
              <Form.Item
                name={`${roleDisabled ? "role-" : ""}permissions~delete-${record.id}`}
                key={`permissions~delete-${record.id}`}
                noStyle
                valuePropName="checked"
                initialValue={roleDisabled && val}
              >
                <Checkbox
                  name="delete"
                  disabled={roleDisabled}
                />
              </Form.Item>
            </div>
          </Tooltip>
        ) : (
          <Form.Item
            name={`permissions~delete-${record.id}`}
            key={`permissions~delete-${record.id}`}
            noStyle
            valuePropName="checked"
          >
            <Tooltip
              title={`This permission does not support this change.`}
              placement={"left"}
            >
              <Checkbox name="delete" disabled />
            </Tooltip>
          </Form.Item>
        );
      },
    },
  ];

  render() {
    const {
      visibleNewUserDrawer,
      newUserForm,
      applicationsList,
      rolesList,
      permissionsList,
      dashboardsList,
      hasCreateAccess,
      languageCodes,
    } = this.props.values;
    const { showNewUserDrawer, closeNewUserDrawer, onFinishNewUser } =
      this.props.handlers;
    const { selectedCA, displayedDashboards } = this.state;

    const permissionMap = new Map<string | number, Permission[]>();
    
    permissionsList.forEach((permission) => {
      if (!permission.customerApplication) {
        return;
      }

      if (!permissionMap.has(permission.customerApplication)) {
        permissionMap.set(permission.customerApplication, []);
      }

      const arr = permissionMap.get(permission.customerApplication)!;

      arr.push(permission);
      permissionMap.set(permission.customerApplication, arr);
    });

    return (
      <>
        <ActionButton
          onClick={showNewUserDrawer}
          text="New User"
          type="primary"
          icon={
            !visibleNewUserDrawer ? (
              <PlusCircleOutlined style={{ fontSize: "15px" }} />
            ) : (
              <MinusCircleOutlined style={{ fontSize: "15px" }} />
            )
          }
          disabled={!hasCreateAccess}
        />
        <Drawer
          title="Add New User"
          placement="right"
          onClose={() => this.closeDrawer(closeNewUserDrawer)}
          open={visibleNewUserDrawer}
          width={550}
          closable
          className="manage-pages-drawer new-user-drawer"
          forceRender
        >
          <Form
            form={newUserForm}
            ref={this.formRef}
            layout="vertical"
            onFinish={(values) => {
              onFinishNewUser(values);
              this.closeDrawer(closeNewUserDrawer);
            }}
            name="newUserForm"
            className="new-user-form"
          >
            <Form.Item
              name="firstNameValue"
              label="First Name"
              rules={[
                { required: true, message: "Please type the first name" },
              ]}
            >
              <Input placeholder="Ex: John" />
            </Form.Item>
            <Form.Item
              name="lastNameValue"
              label="Last Name"
              rules={[{ required: true, message: "Please type the last name" }]}
            >
              <Input placeholder="Ex: Smith" />
            </Form.Item>
            <Form.Item
              name="emailValue"
              label="Email"
              rules={[
                { required: true, message: "Please type the email" },
                { type: "email", message: "The email is not valid." },
              ]}
            >
              <Input placeholder="Ex: john@rws.com" />
            </Form.Item>
            <Form.Item
              label="Target Languages"
              shouldUpdate={(previous, current) =>
                previous.targetLanguagesValue !== current.targetLanguagesValue
              }
            >
              {({ getFieldValue }) => {
                const selectedLanguages =
                  getFieldValue("targetLanguagesValue") || [];
                const hasAllLanguagesSelected =
                  languageCodes.length > 0 &&
                  languageCodes.every((language) =>
                    selectedLanguages.includes(language.value),
                  );

                return (
                  <Form.Item
                    name="targetLanguagesValue"
                    noStyle
                    getValueFromEvent={(values: string[]) =>
                      values.includes(SELECT_ALL_LANGUAGES)
                        ? hasAllLanguagesSelected
                          ? []
                          : languageCodes.map((language) => language.value)
                        : values
                    }
                  >
                    <Select
                      mode="multiple"
                      allowClear
                      placeholder="Select target languages"
                      style={{ width: "100%" }}
                      showSearch
                      options={[
                        {
                          label: hasAllLanguagesSelected
                            ? "Remove All"
                            : "Select All",
                          value: SELECT_ALL_LANGUAGES,
                        },
                        ...languageCodes.map((opt) => ({
                          label: opt.title,
                          value: opt.value,
                        })),
                      ]}
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>
            <Row>
              <Col span={8}>
                <Form.Item
                  label="Admin"
                  name="adminValue"
                  valuePropName="checked"
                  initialValue={false}
                >
                  <Switch defaultChecked={false} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="Vendor Manager"
                  name="vendorManagerValue"
                  valuePropName="checked"
                  initialValue={false}
                >
                  <Switch defaultChecked={false} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="Active"
                  name="isActiveValue"
                  valuePropName="checked"
                  initialValue={false}
                >
                  <Switch defaultChecked={false} />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item
              label="Customer Applications"
              name="customerApplicationsValue"
              rules={[
                {
                  required: true,
                  message: "Please select customer application",
                },
              ]}
            >
              <Select
                placeholder="Select application"
                onChange={this.handleChangeApplications}
                mode="multiple"
              >
                {applicationsList
                  ?.filter((i) => i.active)
                  .map((app) => (
                    <Option value={app._id} key={app._id}>
                      {capitalizeString(app.name)}
                    </Option>
                  ))}
              </Select>
            </Form.Item>

            {selectedCA.size === 0 ? (
              <p>Please select customer application first</p>
            ) : (
              <>
                <Form.Item name="dashboardsValue" label="Dashboards">
                  <Select
                    mode="multiple"
                    placeholder="Select dashboards"
                    style={{ width: "100%" }}
                  >
                    {displayedDashboards.map((dashboard) => (
                      <Option
                        value={dashboard.id}
                        key={dashboard.id}
                        title={dashboard.name}
                      >
                        {dashboard.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  name="rolesValue"
                  label="Roles"
                  rules={[
                    {
                      required: true,
                      message: "Please select at least 1 role",
                    },
                  ]}
                >
                  <Select
                    mode="multiple"
                    placeholder="Select roles"
                    style={{ width: "100%" }}
                    onChange={this.handleChangeRoles}
                  >
                    {rolesList
                      ?.filter(
                        (r) =>
                          selectedCA.has(
                            r.customerApplication as string
                          ) && r.isActive
                      )
                      .map((role) => (
                        <Option
                          value={role.id}
                          key={role.id}
                          title={role.description}
                        >
                          {capitalizeString(role.name)}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
                <Form.Item label="Permissions">
                  {Array.from(selectedCA).map((ca) => {
                    if (!permissionMap.get(ca)) {
                      return null;
                    }
                    const [application] = applicationsList.filter(
                        (a) => a._id === ca
                      ),
                      list = permissionMap.get(ca)!;

                    const groupped = groupAndSortByKey(list, "category");

                    const renderedPermissions: React.ReactNode[] = [];

                    for (const [category, categoryList] of Object.entries(
                      groupped
                    )) {
                      const permissionGroup = (
                        <Table
                          columns={this.columns}
                          dataSource={(categoryList as Permission[]).sort(
                            (a, b) => a.name.localeCompare(b.name)
                          )}
                          title={() => (
                            <Tooltip
                              title={`${capitalizeString(
                                category
                              )} category is part of ${capitalizeString(
                                application.name
                              )} customer application.`}
                              color={"var(--tertiary-color)"}
                              placement={"left"}
                            >
                              <span style={{ cursor: "help" }}>
                                {application.name}
                                <RightOutlined
                                  style={{
                                    color: "var(--primary-color)",
                                    margin: "0 3px",
                                    fontSize: "12px",
                                  }}
                                />
                                {category}
                              </span>
                            </Tooltip>
                          )}
                          key={`${application._id}-${category}`}
                          pagination={false}
                          className="role-table"
                        />
                      );

                      renderedPermissions.push(permissionGroup);
                    }

                    return renderedPermissions;
                  })}
                </Form.Item>
              </>
            )}
            <Form.Item>
              <Button type="primary" htmlType="submit">
                Save User
              </Button>
            </Form.Item>
          </Form>
        </Drawer>
      </>
    );
  }
}

interface AddUserWrapperProps {
  handlers: AddUserHandlers;
  values: AddUserValues;
}

const AddUserWrapper: React.FC<AddUserWrapperProps> = ({ handlers, values }) => {
  useDrawerScrollMargin('', values.visibleNewUserDrawer);
  return <AddUser handlers={handlers} values={values} />
};

export default AddUserWrapper;
